import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithTokenHandling } from '../baseQuery';
import { CODES_TAG_TYPE } from './constants';
import type { CodeSearchResult } from './types';

export interface Modifier {
  code: string;
  description: string;
}

interface ModifiersResponse {
  results: Modifier[];
  next: string | null;
  count: number;
}

interface ModifiersQueryArg {
  q?: string;
  pageSize?: number;
  sort?: string;
  order?: string;
}

export const codesApi = createApi({
  reducerPath: 'codes',
  baseQuery: baseQueryWithTokenHandling,
  tagTypes: [CODES_TAG_TYPE],
  endpoints: (builder) => ({
    getCodeByName: builder.query<{ results: CodeSearchResult[] }, { codeIds: string[] }>({
      query: ({ codeIds }) => {
        // Build query string with multiple code parameters using the same pattern as strategyApi
        const searchParams = new URLSearchParams();

        if (codeIds && codeIds.length > 0) {
          codeIds.forEach((codeId) => searchParams.append('code', codeId));
        }

        return {
          url: `v1/search/codes/?${searchParams.toString()}`,
        };
      },

      providesTags: (result) => {
        if (!result) return [];
        const codes = Array.isArray(result) ? result : [];
        return codes.map(({ id }: any) => ({
          type: CODES_TAG_TYPE,
          id,
        }));
      },
    }),

    getCodeSuggestions: builder.query<CodeSearchResult[] | { results: CodeSearchResult[] }, { q: string }>({
      query: ({ q }) => ({
        url: 'v1/search/codes/',
        params: { q },
      }),
      providesTags: (result) => {
        if (!result) return [];
        const codes = 'results' in result ? result.results : result;
        return Array.isArray(codes)
          ? codes.map(({ code_name }: CodeSearchResult) => ({
              type: CODES_TAG_TYPE as typeof CODES_TAG_TYPE,
              id: code_name,
            }))
          : [];
      },
    }),
    getModifiers: builder.infiniteQuery<
      ModifiersResponse,
      ModifiersQueryArg,
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage, _allPages, lastPageParam) =>
          lastPage.next ? lastPageParam + 1 : undefined,
      },
      query({ queryArg, pageParam }) {
        const { q, pageSize, sort, order } = queryArg;
        return {
          url: 'v1/search/modifiers/',
          params: {
            page: pageParam,
            ...(q ? { q } : {}),
            ...(pageSize ? { pageSize } : {}),
            ...(sort ? { sort } : {}),
            ...(order ? { order } : {}),
          },
        };
      },
    }),
  }),
});

export const {
  useGetCodeByNameQuery,
  useGetCodeSuggestionsQuery,
  useGetModifiersInfiniteQuery,
} = codesApi;
